/*!
 *  (c)(r) 2008-2023 IT-Service Robert Frunzke
 */

import { arr_inArray, mj_deep_extend } from './util.js';


class BasicSearchRequest {

	_state;
	_transient_title = '';
	_object_id = 0;
	_additional_data = '';

	constructor( state ) {
		const defaults = {
			'fulltext': null,					// null or string containing the fulltext query
			'fulltext_multiline': false,
			'constraints': [],
			'mode': null,						// null, 'browse', 'filter'
			'colors': ['','',''],				// array of up to three color codes, each either empty or hexrgb code (e.g. 'aabb33')
			'options': [],
			'in_trees': [],						// array of numeric tree_ids
			'in_categories': [],				// array of numeric cat_ids
			'not_in_trees': [],					// array of numeric tree_ids
			'metasearch_trees': [],
			'metasearch_values': [],
			'conjunct_mode_trees': [],
		};

		// init request state data
		// -> ensure _state becomes independent object (a deep copy of defaults plus provided state)
		this._state = mj_deep_extend( {}, defaults, state||{} );

		if( this._state.hasOwnProperty('_transient_title') ) {
			this._transient_title = this._state._transient_title;
			delete this._state._transient_title;
		}
		if( this._state.hasOwnProperty('_object_id') ) {
			this._object_id = this._state._object_id;
			delete this._state._object_id;
		}
		if( this._state.hasOwnProperty('_additional_data') ) {
			this._additional_data = this._state._additional_data;
			delete this._state._additional_data;
		}
	}


	static createOptionKey( opt_cfg_or_meta_id, alt_type ) {
		if( typeof opt_cfg_or_meta_id === 'object' ) {
			return opt_cfg_or_meta_id.meta_id + ':' + ( alt_type ?? opt_cfg_or_meta_id.type );
		}
		return ( opt_cfg_or_meta_id ?? '' ) + ':' + ( alt_type ?? '' );
	}


	newInstance() {
		// console.log('BasicSearchRequest::newInstance()');
		return new BasicSearchRequest(this._state);
	}

	getSerializedState( pretty ) {
		return JSON.stringify(this._state,null,pretty?' ':null);
	}

	getState() {
		return this._state;
	}

	getTransientState() {
		return Object.assign( {}, this._state, { _transient_title:this._transient_title } );
	}

	setTransientTitle( title ) {
		this._transient_title = title;
	}

	getTransientTitle() {
		return this._transient_title;
	}

	createCopy() {
		let new_sr = this.newInstance();
		new_sr.setTransientTitle(this.getTransientTitle());
		new_sr.setObjectId(this.getObjectId());
		new_sr.setAdditionalData(this.getAdditionalData());
		return new_sr;
	}

	setFulltextQuery( ftq, ftm ) {
		this._state.fulltext = ftq;
		this._state.fulltext_multiline = ftm;
	}

	setConstraints( constraints ) {
		this._state.constraints = constraints;
	}

	hasConstraints() {
		return this._state.constraints.length!==0;
	}

	removeConstraint( constraint ) {
		const l = this._state.constraints.length;
		for( var i=0; i<l; ++i ) {
			if( this._state.constraints[i]==constraint ) {
				this._state.constraints.splice(i,1);
				return true;
			}
		}
		return false;
	}

	setBrowseMode( mode ) {
		this._state.mode = mode;
	}

	removeColorByCode( code ) {
		const cl = this._state.colors.length;
		for( var i=0; i<cl; ++i ) {
			if( this._state.colors[i]===code ) this._state.colors[i] = '';
		}
	}

	removeColorByIndex( index ) {
		if( index >= 0 && index < this._state.colors.length ) this._state.colors[index] = '';
	}

	removeColors() {
		this._state.colors = ['','',''];
	}

	removeOptionByKey( key ) {
		let ol = this._state.options.length;
		for( let i=0; i<ol; ) {
			if( key===BasicSearchRequest.createOptionKey(this._state.options[i].config) ) {
				this._state.options.splice(i,1);
				--ol;
			} else {
				++i;
			}
		}
	}

	setOption( config, value, display_value ) {
		const skey = BasicSearchRequest.createOptionKey(config);
		const oobj = { config:config, value:value, display_value:display_value };
		const ol = this._state.options.length;
		for( var i=0; i<ol; ) {
			if( skey===BasicSearchRequest.createOptionKey(this._state.options[i].config) ) {
				this._state.options[i] = oobj;
				return;
			}
		}
		this._state.options.push(oobj);
	}

	removeOptions() {
		this._state.options = [];
	}

	setCategoryXFilter( filter ) {
		this._state.in_trees = filter.hasOwnProperty('in_trees') ? filter.in_trees.slice(0) : [];
		this._state.in_categories = filter.hasOwnProperty('categories') ? filter.categories.slice(0) : [];
		this._state.not_in_trees = filter.hasOwnProperty('not_in_trees') ? filter.not_in_trees.slice(0) : [];
		this._state.metasearch_trees = filter.hasOwnProperty('metasearch_trees') ? filter.metasearch_trees.slice(0) : [];
		this._state.metasearch_values = filter.hasOwnProperty('metasearch_values') ? filter.metasearch_values.slice(0) : [];
	}

	getCategoryXFilter() {
		return {
			in_trees: this._state.in_trees.slice(0),
			categories: this._state.in_categories.slice(0),
			not_in_trees: this._state.not_in_trees.slice(0),
			metasearch_trees: this._state.metasearch_trees.slice(0),
			metasearch_values: this._state.metasearch_values.slice(0),
		};
	}

	removeFromCategoriesFilter( tree_id, cat_ids ) {
		if( cat_ids && cat_ids.length!==0 ) {
			this._state.in_categories = this._state.in_categories.filter( function(c) {
				return arr_inArray(c,cat_ids)===-1;
			} );
			this._state.metasearch_values = this._state.metasearch_values.filter( function(v) {
				return arr_inArray(v.cat_id,cat_ids)===-1;
			} );
			if( arr_inArray(0,cat_ids)!==-1 ) {
				this._state.not_in_trees = this._state.not_in_trees.filter( function(t) {
					return t != tree_id;
				} );
			}
			// TODO: update in_trees too ???
			// TODO: update metasearch_trees too ???
		}
	}

	removeCategoriesFilter() {
		this._state.in_trees = [];
		this._state.in_categories = [];
		this._state.not_in_trees = [];
		this._state.metasearch_trees = [];
		this._state.metasearch_values = [];
		this._state.conjunct_mode_trees = [];
	}

	getMetaSearchValues() {
		return this._state.metasearch_values;
	}

	hasCategoriesFilter() {
		return this._state.in_trees.length!==0 || this._state.in_categories.length!==0 || this._state.not_in_trees.length!==0 || this._state.metasearch_trees.length!==0 || this._state.metasearch_values.length!==0;
	}
	hasOptions() {
		return this._state.options.length!==0;
	}
	hasColors() {
		const cl = this._state.colors.length;
		if( cl!==0 ) {
			for( var i=0; i<cl; ++i ) {
				if( this._state.colors[i]!=='' ) return true;
			}
		}
		return false;
	}

	normalize() {
		if( this._state.mode===null ) this._state.mode = 'browse';
		const cl = this._state.colors;
		if( cl<3 ) {
			for( var i=cl; i<3; ++i ) this._state.colors.push('');
		}
		if( this._state.fulltext===null || this._state.fulltext==='' ) {
			if( this.hasCategoriesFilter() || this.hasOptions() || this.hasColors() ) this._state.fulltext = '*';
		}
	}

	deflateForURLRequest() {
		if( this._state.hasOwnProperty('options') ) {
			const so = this._state.options;
			const sol = so.length;
			for( var i=0; i<sol; ++i ) {
				const o = so[i];
				if( o.hasOwnProperty('display_value') ) delete o.display_value;
				if( o.hasOwnProperty('config') ) {
					const oc = o.config;
					if( oc.hasOwnProperty('title') ) delete oc.title;
					if( oc.hasOwnProperty('change') ) delete oc.change;
					if( oc.hasOwnProperty('config') ) delete oc.config;
				}
			}
		}
	}

	inflateOptions( query_title_cb, query_display_value_cb ) {
		// for each option: determine title and compute display_value
		if( this._state.hasOwnProperty('options') ) {
			const so = this._state.options;
			const sol = so.length;
			for( var i=0; i<sol; ++i ) {
				const o = so[i];
				const oc = o.hasOwnProperty('config') ? o.config : {};
				if( !oc.hasOwnProperty('title') ) {
					oc.title = query_title_cb(oc);
					if( oc.title===null ) oc.title = oc.meta_id+':'+oc.type;
				}
				if( !o.hasOwnProperty('display_value') ) {
					o.display_value = query_display_value_cb(oc,o.value);
					if( o.display_value===null ) o.display_value = o.value;
				}
			}
		}
	}

	normalized() {
		let new_sr = this.newInstance();
		new_sr.normalize();
		new_sr.setTransientTitle(this.getTransientTitle());
		new_sr.setObjectId(this.getObjectId());
		new_sr.setAdditionalData(this.getAdditionalData());
		return new_sr;
	}

	deflatedForURLRequest() {
		let new_sr = this.newInstance();
		new_sr.normalize();
		// new_sr = new_sr.withCategoriesDeflated();
		new_sr.deflateForURLRequest();
		new_sr.setTransientTitle(this.getTransientTitle());
		new_sr.setObjectId(this.getObjectId());
		new_sr.setAdditionalData(this.getAdditionalData());
		return new_sr;
	}

	setObjectId(id) {
		this._object_id = id;
	}
	getObjectId() {
		return this._object_id;
	}
	setAdditionalData(data) {
		this._additional_data = data;
	}
	getAdditionalData() {
		return this._additional_data;
	}

}



function createBasicSearchRequest( state ) {
	return new BasicSearchRequest(state);
}

function unserializeBasicSearchRequest( state_json ) {
	return createBasicSearchRequest(JSON.parse(state_json));
}





export {
	BasicSearchRequest,
	createBasicSearchRequest,
	// eslint-disable-next-line import/no-unused-modules
	unserializeBasicSearchRequest,
};

